/**
 * Pipeline-Werkstatt — Glass-Box-Doku + Dev-Environment der Produktions-Pipeline.
 *
 * 7 Phasen, jede ein Tab. Output von Phase N ist Input von Phase N+1.
 * Single-Source-Prinzip: was hier sichtbar ist, läuft 1:1 in Production.
 *
 * Einstieg: Phase 1 lädt PDF hoch und erzeugt die Karte. Card-ID landet als
 * ?card=<id> in der URL und persistiert über alle Phasen. Footer-Nav schaltet
 * "Weiter zu Phase N+1" nur frei wenn die Completion-Gate der aktuellen Phase
 * erfüllt ist.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api, type Card, type Employee, type Timesheet } from "../lib/api";
import { useClientContext } from "../lib/ClientContext";
import { DetailModal } from "../components/DetailModal";
import { Phase1Upload } from "../components/werkstatt/Phase1Upload";
import { Phase2Ocr } from "../components/werkstatt/Phase2Ocr";
import { Phase3Validation } from "../components/werkstatt/Phase3Validation";
import { Phase4Berechnung } from "../components/werkstatt/Phase4Berechnung";
import { Phase5Review } from "../components/werkstatt/Phase5Review";
import { Phase6Export } from "../components/werkstatt/Phase6Export";
import { Phase7Konflikt } from "../components/werkstatt/Phase7Konflikt";
import { LiveActivityLog } from "../components/werkstatt/LiveActivityLog";
import { SimulationPanel } from "../components/werkstatt/SimulationPanel";
import { PipelineFlowDiagram } from "../components/werkstatt/PipelineFlowDiagram";
import { WerkstattBranchHeader } from "../components/werkstatt/WerkstattBranchHeader";
import { nextPhaseFor, phaseReachable, phaseStatus, PIPELINE_FLOW } from "../lib/pipelineFlow";

const PHASES = [
  { slug: "upload", num: 1, title: "Upload + Triage" },
  { slug: "ocr", num: 2, title: "OCR" },
  { slug: "validierung", num: 3, title: "Validierung" },
  { slug: "berechnung", num: 4, title: "Berechnung" },
  { slug: "review", num: 5, title: "Review" },
  { slug: "export", num: 6, title: "Export RZL" },
  { slug: "konflikt", num: 7, title: "Konflikte" },
] as const;

export type Phase = typeof PHASES[number]["slug"];

export interface WerkstattCardContext {
  card: Card | null;
  timesheet: Timesheet | null;
  employee: Employee | null;
  employees: Employee[];
  allCards: Card[];
  reload: () => void;
}

/**
 * Real state machine (from hub/src/db/migrations/019_ocr_running_state.sql):
 *   uploaded → ocr_pending → ocr_running → ocr_complete → validated → calculated → exported
 *                                              ↘ pending_conflict        archived/superseded
 *                          ↘ pending_review (manual disambiguation)
 *
 * Phase 1 "done" = OCR läuft oder ist fertig (state ≥ ocr_pending).
 */
export function phaseComplete(phase: Phase, card: Card | null): boolean {
  if (!card) return false;
  const s = card.state;
  // Phase 1 ist über Upload+Triage. pending_conflict entsteht ERST in Phase 2 wenn
  // der Worker den Employee auflöst — kann logisch nie in Phase 1 verhindert oder
  // gelöst werden. Daher zählt pending_conflict als "Phase 1 fertig", der Konflikt
  // wird in Phase 2 / Phase 7 gezeigt.
  const PROGRESSING_OCR = ["ocr_pending", "ocr_running", "ocr_complete", "validated", "calculated", "exported", "pending_conflict", "archived", "superseded"];
  // OCR-Output existiert auch bei archived/superseded (Audit-Trail) — Phase 2 zählt als "done"
  const OCR_OUTPUT_EXISTS = ["ocr_complete", "validated", "calculated", "exported", "archived", "superseded"];
  switch (phase) {
    case "upload":
      return PROGRESSING_OCR.includes(s);
    case "ocr":
      return OCR_OUTPUT_EXISTS.includes(s);
    case "validierung":
      return ["validated", "calculated", "exported"].includes(s);
    case "berechnung":
      return ["calculated", "exported"].includes(s);
    case "review":
      return ["calculated", "exported"].includes(s);
    case "export":
      return s === "exported";
    case "konflikt":
      return s !== "pending_conflict";
  }
}

export function phaseGateReason(phase: Phase, card: Card | null): string {
  if (!card) return "Keine Karte — erst in Phase 1 hochladen";
  const s = card.state;
  if (phase === "upload") {
    if (s === "uploaded") return "Manual-Mode: 'OCR starten'-Button in Phase 1 klicken";
    if (s === "pending_conflict") return "Conflict-Behandlung nötig — siehe Phase 7";
    if (s === "pending_review") return "Manual-Disambiguation nötig (kind=unknown)";
    if (s === "sonstige") return "Sonstige — nicht relevant (Karte 589); per Deklaration umwidmbar";
    return `state=${s}`;
  }
  if (phase === "ocr") {
    if (!phaseComplete("upload", card)) return "Phase 1 (Upload+Triage) noch nicht abgeschlossen";
    if (s === "ocr_pending") return "Warte auf Worker-Pick (ocr_pending)";
    if (s === "ocr_running") return "OCR läuft… (typisch 5-10 min bei Qwen3-VL CPU)";
    return `state=${s}`;
  }
  if (phase === "validierung") return s === "ocr_complete" ? "Warte auf User-Klick 'Validieren'" : `state=${s}`;
  if (phase === "berechnung") return `Warte auf Worker-Job calculate (state=${s})`;
  if (phase === "review") return "Karte muss mind. state=calculated haben";
  if (phase === "export") return "Karte muss state=exported haben";
  return s;
}

export function WerkstattPage({ modal }: { modal?: boolean } = {}) {
  const loc = useLocation();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  // Modal-Mode wird von App.tsx über das zweite <Routes>-Overlay gesetzt, wenn
  // location.state.background existiert. Close-Button kehrt dann zur darunter-
  // liegenden Route (Kanban/Inbox/etc) zurück, sodass der Kontext sichtbar bleibt.
  const background = (loc.state as { background?: Location } | null)?.background;
  function closeModal() {
    if (background) nav(-1);
    else nav("/board");
  }
  const { selectedClientId: clientId } = useClientContext();

  const activePhase: Phase = useMemo(() => {
    const parts = loc.pathname.split("/").filter(Boolean);
    const last = parts[parts.length - 1];
    const found = PHASES.find(p => p.slug === last);
    return found?.slug ?? "upload";
  }, [loc.pathname]);

  const cardId = sp.get("card") ?? "";

  const [cards, setCards] = useState<Card[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [timesheet, setTimesheet] = useState<Timesheet | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  // Toggle between text-list view and graphical flow diagram. Default: text
  // (current default), user can switch to diagram for visual quality gate.
  const [showDiagram, setShowDiagram] = useState<boolean>(() =>
    localStorage.getItem("werkstatt_view") === "diagram"
  );
  function toggleView() {
    const next = !showDiagram;
    setShowDiagram(next);
    localStorage.setItem("werkstatt_view", next ? "diagram" : "text");
  }

  useEffect(() => {
    if (!clientId) return;
    // Load last 6 months. Build "YYYY-MM" strings directly to avoid timezone
    // truncation: `new Date(2026, 4, 1).toISOString()` shifts to April UTC in
    // CEST and yields "2026-04" — this bug hid the just-uploaded May card.
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const months: string[] = [];
    for (let i = 0; i < 6; i++) {
      let mm = m - i;
      let yy = y;
      while (mm < 0) { mm += 12; yy -= 1; }
      months.push(`${yy}-${String(mm + 1).padStart(2, "0")}`);
    }
    Promise.all([
      Promise.all(months.map(m => api.cards.list(m, "", clientId))).then(rs => rs.flatMap(r => r.cards)),
      api.employees.list(clientId),
    ]).then(([allCards, emps]) => {
      // Dedup by id
      const seen = new Set<string>();
      const uniq: Card[] = [];
      for (const c of allCards) {
        if (!seen.has(c.id)) { seen.add(c.id); uniq.push(c); }
      }
      setCards(uniq);
      setEmployees(emps);
    }).catch(e => setErr(e.message));
  }, [clientId, reloadKey]);

  useEffect(() => {
    if (!cardId) { setTimesheet(null); setErr(null); return; }
    setErr(null);
    // Kanonischer Card-Lookup — auto-detected ob timesheet oder expense.
    // Vor 2026-05-19 war hier api.timesheets.get hartcoded → 404 für jede
    // expense-Karte. Glass-Box-Drift, sichtbar gemacht durch den Dev-Karten-
    // Selector (Demo-Diäten-Pille kannst ohne Fetch-Error nutzen).
    api.cards.byId(cardId)
      .then((card) => setTimesheet(card as unknown as Timesheet))
      .catch(e => setErr(e.message));
  }, [cardId, reloadKey]);

  // Single-source for the active card. Falls back to the loaded timesheet so the
  // UI doesn't freeze on "no card" the moment after an upload (cards.list is on
  // a separate fetch loop and may lag behind the just-uploaded card).
  const selectedCard = useMemo<Card | null>(() => {
    const fromList = cards.find(c => c.id === cardId);
    if (fromList) return fromList;
    if (timesheet && timesheet.id === cardId) {
      // `timesheet` ist hier das Generic-Card-Objekt aus api.cards.byId
      // (kann timesheet, expense oder unknown sein). Es bringt `kind` und
      // bei joinbarem Employee auch first_name/last_name/dienstnehmernummer
      // mit (Hub-Query joint employees). Wir reichen es 1:1 als Card weiter.
      const fromFetch = timesheet as unknown as Card;
      return fromFetch;
    }
    return null;
  }, [cards, cardId, timesheet, employees]);
  const selectedEmp = selectedCard?.employee_id
    ? employees.find(e => e.id === selectedCard.employee_id) ?? null
    : null;

  // Auto-poll while the card is in a transient/worker-owned state — let
  // the user watch state advance without manual refresh.
  useEffect(() => {
    if (!selectedCard) return;
    const transient = ["ocr_pending", "ocr_running", "validated"].includes(selectedCard.state);
    if (!transient) return;
    const t = setTimeout(() => setReloadKey(k => k + 1), 5000);
    return () => clearTimeout(t);
  }, [selectedCard?.state, selectedCard?.id, reloadKey]);

  function setCardId(id: string) {
    const next = new URLSearchParams(sp);
    if (id) next.set("card", id); else next.delete("card");
    setSp(next, { replace: true });
    setReloadKey(k => k + 1);
  }

  function goPhase(slug: Phase) {
    nav(`/werkstatt/abrechnung/${slug}${cardId ? `?card=${cardId}` : ""}`);
  }

  const ctx: WerkstattCardContext = {
    card: selectedCard,
    timesheet,
    employee: selectedEmp,
    employees,
    allCards: cards,
    reload: () => setReloadKey(k => k + 1),
  };

  const idx = PHASES.findIndex(p => p.slug === activePhase);
  const prev = idx > 0 ? PHASES[idx - 1]! : null;
  // Branching-aware next-phase resolution. Replaces linear "PHASES[idx+1]"
  // with a state-machine lookup from lib/pipelineFlow.ts. Handles:
  //   - terminal states (archived/superseded/exported) → END
  //   - conflict branch (pending_conflict → konflikt)
  //   - re-entry after conflict (konflikt resolved → validierung)
  const nextResolution = nextPhaseFor(activePhase, selectedCard, timesheet);

  const body = (
    <div className="werkstatt-page">
      <WerkstattBranchHeader activeBranch="abrechnung" modal={modal} />
      <p className="muted" style={{ margin: "0 0 12px 0", maxWidth: 820 }}>
        Phase 1 ist der Einstieg — dort wird die PDF hochgeladen. Nachfolgende
        Phasen sind erst betretbar wenn die vorherige abgeschlossen ist.
        {selectedCard && (
          <>
            {" "}<strong>Aktive Karte:</strong>{" "}
            <code>{selectedCard.id.slice(0, 8)}</code>{" "}
            <CardStateBadge state={selectedCard.state} />
            {" "}<button
              type="button"
              onClick={() => setCardId("")}
              className="link"
              style={{
                background: "none", border: "none", color: "#6b7280",
                cursor: "pointer", padding: "0 0 0 6px", fontSize: 12,
              }}
              title="Karte aus URL entfernen — zurück zu Phase 1 Upload"
            >× zurücksetzen</button>
          </>
        )}
      </p>
      {/* Fehler-Banner in eigener Zeile rendern statt inline an „× zurücksetzen"
          zu kleben. Sonst entstanden Lesefehler wie „zurücksetzenFehler:
          Eintrag nicht gefunden" als ein Wort. */}
      {err && (
        <div style={{
          color: "#991b1b", background: "#fef2f2",
          border: "1px solid #fca5a5", padding: "6px 10px",
          borderRadius: 4, marginBottom: 12, fontSize: 13,
        }}>
          Fehler: {err}
        </div>
      )}

      <div className="werkstatt-tabs">
        {PHASES.map(p => {
          // Ehrlicher Status aus der Twin-Reise (rooms.ts Single Source):
          // 🟢 done · 🔴 error (Twin hängt hier) · 🔵 active · ⚪ not-reached.
          const st = phaseStatus(p.slug, selectedCard);
          const done = st === "done";
          const isError = st === "error";
          const reachable = phaseReachable(p.slug, selectedCard, timesheet);
          // „active" aufspalten: läuft (Maschine arbeitet) vs. wartet auf dich (manuelles Gate).
          const machineWorking = !!selectedCard && ["ocr_pending", "ocr_running", "validated"].includes(selectedCard.state);
          const isWaiting = st === "active" && !machineWorking;
          const glyph = st === "done" ? "✓" : st === "error" ? "✗"
                    : st === "active" ? (machineWorking ? "▶" : "✋") : p.num;
          const tip = isError ? `Phase ${p.num}: ${p.title} — 🔴 Fehler, Twin hängt hier`
                    : isWaiting ? `Phase ${p.num}: ${p.title} — ✋ wartet auf dich (manuelle Aktion)`
                    : st === "active" ? `Phase ${p.num}: ${p.title} — ▶ läuft gerade`
                    : done ? `Phase ${p.num}: ${p.title} — ✓ abgeschlossen`
                    : !reachable ? "Phase noch nicht erreicht (Twin nie angekommen)"
                    : `Phase ${p.num}: ${p.title}`;
          return (
            <button
              key={p.slug}
              type="button"
              className={"werkstatt-tab " + (activePhase === p.slug ? "active" : "") + (done ? " done" : "") + (isError ? " error" : "")}
              onClick={() => goPhase(p.slug)}
              disabled={!reachable && !isError}
              title={tip}
              style={isError ? { borderColor: "#dc2626", color: "#991b1b" } : undefined}
            >
              <span className="werkstatt-tab-num"
                    style={isError ? { background: "#dc2626", color: "#fff", borderColor: "#dc2626" } : undefined}>
                {glyph}
              </span>
              <span className="werkstatt-tab-title">{p.title}</span>
            </button>
          );
        })}
      </div>

      {selectedCard && selectedCard.kind !== "unknown" && (
        <SimulationPanel
          cardId={selectedCard.id}
          kind={selectedCard.kind as "timesheet" | "expense"}
          cardState={selectedCard.state}
          refreshKey={reloadKey}
          onReset={() => setReloadKey(k => k + 1)}
        />
      )}

      {selectedCard && (
        <LiveActivityLog
          card={selectedCard}
          timesheet={timesheet}
          pollIntervalMs={3000}
          onPolled={() => setReloadKey(k => k + 1)}
        />
      )}

      {selectedCard && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <button
            type="button"
            onClick={toggleView}
            className="link"
            style={{
              background: showDiagram ? "#1d4ed8" : "transparent",
              color: showDiagram ? "white" : "#1d4ed8",
              border: "1px solid #1d4ed8",
              padding: "4px 12px", borderRadius: 4, cursor: "pointer", fontSize: 12,
            }}
          >
            {showDiagram ? "📊 Diagramm ↔ 📝 Text-Liste" : "📝 Text-Liste ↔ 📊 Diagramm"}
          </button>
        </div>
      )}

      {selectedCard && showDiagram && (
        <PipelineFlowDiagram
          card={selectedCard}
          timesheet={timesheet}
          currentPhase={activePhase}
        />
      )}

      <div className="werkstatt-body" style={{ marginTop: 12 }}>
        {activePhase === "upload" && <Phase1Upload ctx={ctx} setCardId={setCardId} />}
        {activePhase === "ocr" && <Phase2Ocr ctx={ctx} />}
        {activePhase === "validierung" && <Phase3Validation ctx={ctx} />}
        {activePhase === "berechnung" && <Phase4Berechnung ctx={ctx} />}
        {activePhase === "review" && <Phase5Review ctx={ctx} />}
        {activePhase === "export" && <Phase6Export ctx={ctx} />}
        {activePhase === "konflikt" && <Phase7Konflikt ctx={ctx} />}
      </div>

      <div className="werkstatt-footer-nav">
        {prev && (
          <Link to={`/werkstatt/abrechnung/${prev.slug}${cardId ? `?card=${cardId}` : ""}`} style={{ fontSize: 13 }}>
            ← Phase {prev.num}: {prev.title}
          </Link>
        )}
        <span style={{ flex: 1 }} />

        {/* Branching-aware next button: terminal states show "Pipeline beendet",
            wait-states show grey "Warte…", phase-transitions enable the button. */}
        {nextResolution.type === "end" && (
          <span style={{
            padding: "8px 16px", fontSize: 13, fontWeight: 600,
            color: "#6b7280", background: "#f3f4f6",
            border: "1px solid #d1d5db", borderRadius: 4,
          }}>
            🏁 {nextResolution.label}
          </span>
        )}
        {nextResolution.type === "wait" && (
          <button
            type="button"
            disabled
            className="werkstatt-next-btn"
            title={nextResolution.label}
          >
            ⏸ {nextResolution.label}
          </button>
        )}
        {nextResolution.type === "phase" && nextResolution.target && (
          <button
            type="button"
            onClick={() => goPhase(nextResolution.target!)}
            className="werkstatt-next-btn"
            title={nextResolution.label}
          >
            Weiter zu Phase {nextResolution.num}: {nextResolution.label} →
          </button>
        )}

        {/* "Validierungs-UI öffnen" gehört zu Phase 3 Step 4 (User-Validate-Gate),
            NICHT in den globalen Footer — siehe Glass-Box-Antipattern "Step gehört wo
            er läuft". Der Link ist jetzt in Phase3Validation.tsx inline gerendert. */}
      </div>
    </div>
  );

  // Modal-Mode: in Overlay rendern statt als Full-Page, damit der darunter-
  // liegende Kanban-/Inbox-/Conflict-Stack erhalten bleibt und beim Schließen
  // sichtbar wird.
  if (modal) {
    return (
      <DetailModal dirty={false} onClose={closeModal}>
        {body}
      </DetailModal>
    );
  }
  return body;
}

/** A phase is reachable if all prior sequential phases are complete.
 * Phase 1 (upload) and 7 (konflikt) are always reachable. */
function isReachable(phase: Phase, card: Card | null): boolean {
  if (phase === "upload" || phase === "konflikt") return true;
  if (!card) return false;
  const idx = PHASES.findIndex(p => p.slug === phase);
  for (let i = 0; i < idx; i++) {
    const prior = PHASES[i]!.slug;
    if (prior === "konflikt") continue;
    if (!phaseComplete(prior, card)) return false;
  }
  return true;
}

function CardStateBadge({ state }: { state: string }) {
  const color: Record<string, string> = {
    pending: "#9ca3af",
    pending_review: "#f59e0b",
    sonstige: "#94a3b8",
    pending_conflict: "#dc2626",
    ocr_complete: "#3b82f6",
    validated: "#0ea5e9",
    calculated: "#10b981",
    exported: "#6b7280",
  };
  return (
    <span style={{
      padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: color[state] ?? "#e5e7eb",
      color: "white",
      marginLeft: 4,
    }}>{state}</span>
  );
}
